/**
 * DangoteCarbonValueBridge — Portfolio Intelligence Engine
 * Route: /corporate/value-bridge
 *
 * Role: "Where is the value?"
 * Reads from approved monthly calculation outputs (DangoteContext).
 * Shows ALL identified opportunities with full financial + SDG data.
 * Action: "Promote to Strategy" → advances opportunity to Carbon Strategy page.
 *
 * Architecture position:
 *   Monthly Reporting → Climate → SDG → [CARBON VALUE BRIDGE] → Strategy → Structuring
 */
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDangote } from "@/context/DangoteContext";
import { PageHeader, SectionCard, MetricCard } from "@/components/ds";
import { RefreshCw, ChevronRight, X, ArrowRight, Info, Filter } from "lucide-react";
import client from "@/api/client";

const STATUS_COLORS = {
  identified:              "bg-slate-100 text-slate-500 border-slate-200",
  promoted_to_strategy:    "bg-blue-100 text-blue-700 border-blue-200",
  selected_for_execution:  "bg-teal-100 text-teal-700 border-teal-200",
  sent_to_financing:       "bg-emerald-100 text-emerald-700 border-emerald-200",
  deprioritized:           "bg-red-50 text-red-500 border-red-200",
};
const STATUS_LABELS = {
  identified:              "Identified",
  promoted_to_strategy:    "In Strategy",
  selected_for_execution:  "In Structuring",
  sent_to_financing:       "In Financing",
  deprioritized:           "Deprioritized",
};
const PATHWAY_COLORS = {
  vcm:      "bg-teal-100 text-teal-700 border-teal-200",
  art6:     "bg-blue-100 text-blue-700 border-blue-200",
  internal: "bg-slate-100 text-slate-500 border-slate-200",
};
const SDG_COLORS = { 7:"#FCC30B", 9:"#F36D25", 11:"#F99D26", 12:"#BF8B2E", 13:"#3F7E44" };

// ── Detail Panel ─────────────────────────────────────────────────────────────
function DetailPanel({ opp, onClose, onTransition, transitioning }) {
  if (!opp) return null;
  const canPromote = opp.status === "identified";

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-white border-l border-slate-200 shadow-2xl z-50 flex flex-col"
      data-testid="detail-panel">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50">
        <p className="text-sm font-bold text-slate-900 truncate pr-2">{opp.name}</p>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 flex-shrink-0"><X size={16}/></button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {/* Identity */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          {[["BU",opp.bu_name],["Asset",opp.asset_id],["Driver",opp.driver_label],["Period",opp.period_id]].map(([l,v])=>(
            <div key={l}><p className="text-slate-400">{l}</p><p className="font-semibold text-slate-900">{v}</p></div>
          ))}
        </div>
        {/* Emissions */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-3">
          <p className="text-xs font-bold text-red-700 uppercase tracking-wide mb-2">Emissions</p>
          {[
            ["Baseline",    `${Number(opp.baseline_emissions_yr||0).toLocaleString()} tCO₂e/yr`],
            ["Project",     `${Number(opp.project_emissions_yr||0).toLocaleString()} tCO₂e/yr`],
            ["Reduction",   `${Number(opp.reduction_tco2e_yr||0).toLocaleString()} tCO₂e/yr`],
            ["Reduction %", `${opp.reduction_pct||0}%`],
            ["Basis",       opp.baseline_type === "historical" ? "Month-on-Month (MoM)" : "Industry Benchmark"],
          ].map(([l,v]) => (
            <div key={l} className="flex justify-between text-xs py-1 border-b border-red-100 last:border-0">
              <span className="text-red-600">{l}</span>
              <span className="font-semibold text-red-800">{v}</span>
            </div>
          ))}
        </div>
        {/* Financial A/B */}
        <div className="space-y-2">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
            <p className="text-xs font-bold text-emerald-700 uppercase mb-2">A. Operational Value (no validation dependency)</p>
            {[
              ["Cost Savings",      `$${Number((opp.cost_savings_usd_yr||0)/1e6).toFixed(2)}M/yr`],
              ["Circularity Value", `$${Number((opp.circularity_value_usd_yr||0)/1e6).toFixed(2)}M/yr`],
              ["Total Guaranteed",  `$${Number((opp.guaranteed_value_usd_yr||0)/1e6).toFixed(2)}M/yr`],
            ].map(([l,v]) => (
              <div key={l} className="flex justify-between text-xs py-1 border-b border-emerald-100 last:border-0">
                <span className="text-emerald-700">{l}</span>
                <span className="font-bold text-emerald-800">{v}</span>
              </div>
            ))}
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <p className="text-xs font-bold text-blue-700 uppercase mb-2">B. Conditional Carbon Value (validation required)</p>
            {[
              ["Low  ($15/t)",  `$${Number((opp.carbon_rev_low_usd_yr||0)/1e6).toFixed(2)}M/yr`],
              ["Base ($25/t)",  `$${Number((opp.carbon_rev_base_usd_yr||0)/1e6).toFixed(2)}M/yr`],
              ["High ($40/t)",  `$${Number((opp.carbon_rev_high_usd_yr||0)/1e6).toFixed(2)}M/yr`],
            ].map(([l,v]) => (
              <div key={l} className="flex justify-between text-xs py-1 border-b border-blue-100 last:border-0">
                <span className="text-blue-600">{l}</span>
                <span className="font-bold text-blue-800">{v}</span>
              </div>
            ))}
            <p className="text-[9px] text-red-500 italic mt-1">Not to be included in base financing decisions until validation confirmed.</p>
          </div>
        </div>
        {/* Investment */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
          <p className="text-xs font-bold text-slate-600 uppercase mb-2">Investment</p>
          {[
            ["CAPEX",   `$${Number((opp.capex_usd||0)/1e6).toFixed(1)}M`],
            ["Payback", `${opp.payback_years||"—"}yr`],
            ["IRR",     `${opp.irr_pct||"—"}%`],
          ].map(([l,v]) => (
            <div key={l} className="flex justify-between text-xs py-1">
              <span className="text-slate-500">{l}</span>
              <span className="font-bold text-slate-900">{v}</span>
            </div>
          ))}
        </div>
        {/* SDG tags */}
        {(opp.sdg_tags||[]).length > 0 && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
            <p className="text-xs font-bold text-slate-600 uppercase mb-2">SDG Contribution</p>
            <div className="flex flex-wrap gap-1.5">
              {(opp.sdg_tags||[]).map(sdg => (
                <span key={sdg} className="text-xs font-bold text-white px-2.5 py-1 rounded-full"
                  style={{backgroundColor: SDG_COLORS[sdg] || "#666"}}>
                  SDG {sdg}
                </span>
              ))}
            </div>
          </div>
        )}
        {/* Traceability */}
        {opp.calculation_id && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
            <p className="text-xs font-bold text-slate-600 uppercase mb-1">Calculation Traceability</p>
            <p className="text-[9px] font-mono text-slate-500">ID: {opp.calculation_id}</p>
            <p className="text-[9px] font-mono text-slate-500">Hash: {opp.calc_hash}...</p>
            <p className="text-[9px] text-slate-400 mt-1">Period: {opp.period_id} · {opp.baseline_type} baseline</p>
          </div>
        )}
      </div>
      {canPromote && (
        <div className="px-5 py-4 border-t border-slate-100">
          <button onClick={() => onTransition(opp.opp_id, "promoted_to_strategy")}
            disabled={transitioning}
            className="w-full flex items-center justify-center gap-2 py-3 bg-[#0F172A] text-white text-sm font-bold rounded-xl hover:bg-slate-800 disabled:opacity-50 transition-colors"
            data-testid="promote-to-strategy-btn">
            {transitioning ? <RefreshCw size={14} className="animate-spin"/> : <ArrowRight size={14}/>}
            Promote to Strategy
          </button>
          <p className="text-[9px] text-slate-400 text-center mt-1">
            Sends this opportunity to Carbon Strategy page for prioritization
          </p>
        </div>
      )}
    </div>
  );
}

// ── Scenario View ─────────────────────────────────────────────────────────────
function ScenarioView({ opps }) {
  const scenarios = [5, 10].map(topN => {
    const sorted = [...opps].sort((a,b) => (b.composite_score||0) - (a.composite_score||0));
    const top    = sorted.slice(0, topN);
    const capex   = top.reduce((s,o) => s + (o.capex_usd||0), 0);
    const savings = top.reduce((s,o) => s + (o.cost_savings_usd_yr||0), 0);
    const carbon  = top.reduce((s,o) => s + (o.carbon_rev_base_usd_yr||0), 0);
    const payback = savings > 0 ? (capex / savings).toFixed(1) : "—";
    return { topN, actual: Math.min(topN, opps.length), capex, savings, carbon, payback };
  });

  const COLS = [
    { label: "Opportunities",  fn: s => `${s.actual}`,                         cls: "text-slate-900 font-bold" },
    { label: "Total CAPEX",    fn: s => `$${(s.capex/1e6).toFixed(0)}M`,        cls: "text-slate-900 font-bold" },
    { label: "Annual Savings", fn: s => `$${(s.savings/1e6).toFixed(1)}M/yr`,  cls: "text-emerald-700 font-bold" },
    { label: "Carbon Value",   fn: s => `$${(s.carbon/1e6).toFixed(1)}M/yr`,   cls: "text-blue-700 font-bold" },
    { label: "Blended Payback",fn: s => `${s.payback}yr`,                      cls: "text-slate-700 font-bold" },
  ];

  return (
    <SectionCard>
      <div className="flex items-center gap-2 mb-4">
        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Scenario View</p>
        <span className="text-[9px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">By composite score rank</span>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Executive comparison — what does capital deployment look like if we select the top-ranked opportunities?
      </p>

      {/* Comparison table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="scenario-table">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left text-slate-400 pb-2 pr-4 font-semibold text-[9px] uppercase tracking-wide">Scenario</th>
              {COLS.map(c => (
                <th key={c.label} className="text-right text-slate-400 pb-2 px-3 font-semibold text-[9px] uppercase tracking-wide whitespace-nowrap">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {scenarios.map((s, i) => (
              <tr key={s.topN}
                className={`border-b border-slate-50 last:border-0 ${i === 0 ? "bg-slate-50/50" : ""}`}>
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${i === 0 ? "bg-teal-500" : "bg-blue-400"}`}/>
                    <p className="font-bold text-slate-900 text-sm">Top {s.topN}</p>
                  </div>
                </td>
                {COLS.map(c => (
                  <td key={c.label} className={`py-3 px-3 text-right text-sm ${c.cls}`}>
                    {c.fn(s)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delta row */}
      {opps.length >= 5 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide mb-2">Top 10 vs Top 5 — Marginal impact</p>
          <div className="flex gap-4 flex-wrap">
            {(() => {
              const [s5, s10] = scenarios;
              return [
                ["+ CAPEX",    `+$${((s10.capex - s5.capex)/1e6).toFixed(0)}M`,    "text-slate-700"],
                ["+ Savings",  `+$${((s10.savings - s5.savings)/1e6).toFixed(1)}M/yr`, "text-emerald-700"],
                ["+ Carbon",   `+$${((s10.carbon - s5.carbon)/1e6).toFixed(1)}M/yr`,  "text-blue-700"],
              ].map(([l,v,c]) => (
                <div key={l} className="flex items-center gap-1.5">
                  <span className="text-[9px] text-slate-400">{l}</span>
                  <span className={`text-xs font-bold ${c}`}>{v}</span>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      <p className="text-[9px] text-slate-400 italic mt-3">
        Ranked by composite score (GHG Impact 30% · Financial Return 25% · Payback 20% · CAPEX 10% · SDG 10% · Readiness 5%).
        Indicative only — not investment advice.
      </p>
    </SectionCard>
  );
}

// ── Filter Pill ───────────────────────────────────────────────────────────────
function FilterPill({ label, active, onClick }) {
  return (
    <button onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all whitespace-nowrap ${
        active ? "bg-[#0F172A] text-white border-transparent" : "bg-white border-slate-200 text-slate-500 hover:border-slate-400"
      }`}>
      {label}
    </button>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DangoteCarbonValueBridge() {
  const navigate = useNavigate();
  const { selectedPeriod, period, periods, setSelectedPeriod,
          vbCache, vbLoading, invalidateVb } = useDangote();
  const [selected, setSelected]   = useState(null);
  const [transitioning, setTrans] = useState(false);

  // Filters
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCapex,  setFilterCapex]  = useState("all");
  const [filterIrr,    setFilterIrr]    = useState("all");
  const [filterBu,     setFilterBu]     = useState("all");
  const [filterSdg,    setFilterSdg]    = useState("all");
  const [showFilters,  setShowFilters]  = useState(false);

  const vbData  = selectedPeriod ? (vbCache[selectedPeriod] || null) : null;
  const loading = vbLoading && !vbData;

  const handleTransition = async (opp_id, newStatus) => {
    setTrans(true);
    await client.post(`/dangote/opportunities/${opp_id}/transition`, { status: newStatus });
    invalidateVb(selectedPeriod);
    setSelected(prev => prev?.opp_id === opp_id ? { ...prev, status: newStatus } : prev);
    setTrans(false);
  };

  const portfolio = vbData?.portfolio     || {};
  const opps      = vbData?.opportunities || [];
  const byStatus  = vbData?.by_status     || {};

  // Dynamic filter options (derived from loaded data)
  const buOptions = useMemo(() => {
    const seen = {};
    opps.forEach(o => { if (o.bu_id) seen[o.bu_id] = o.bu_name || o.bu_id; });
    return Object.entries(seen).map(([id, name]) => ({ id, name }));
  }, [opps]);
  const sdgOptions = useMemo(() =>
    [...new Set(opps.flatMap(o => o.sdg_tags||[]))].sort((a,b)=>a-b), [opps]);

  // Priority rank map (sorted by composite_score)
  const rankMap = useMemo(() => {
    const sorted = [...opps].sort((a,b) => (b.composite_score||0) - (a.composite_score||0));
    const m = {};
    sorted.forEach((o,i) => { m[o.opp_id] = i + 1; });
    return m;
  }, [opps]);

  // Tag logic
  const getTag = (o) => {
    const irr = o.irr_pct || 0;
    const pb = o.payback_years || 99;
    const capex = o.capex_usd || 0;
    if (pb <= 3 && capex <= 15_000_000) return { label: "Quick Win", cls: "bg-teal-100 text-teal-700 border-teal-200" };
    if (irr >= 20) return { label: "High IRR", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" };
    if (o.reduction_tco2e_yr >= 200_000) return { label: "Strategic", cls: "bg-blue-100 text-blue-700 border-blue-200" };
    return { label: "Complex", cls: "bg-slate-100 text-slate-500 border-slate-200" };
  };

  // Apply all filters
  const filtered = useMemo(() => opps
    .filter(o => filterStatus === "all" || o.status === filterStatus)
    .filter(o => filterBu === "all" || o.bu_id === filterBu)
    .filter(o => filterSdg === "all" || (o.sdg_tags||[]).includes(Number(filterSdg)))
    .filter(o => {
      if (filterCapex === "all") return true;
      const c = o.capex_usd || 0;
      if (filterCapex === "lt10")  return c < 10_000_000;
      if (filterCapex === "10-25") return c >= 10_000_000 && c <= 25_000_000;
      if (filterCapex === "gt25")  return c > 25_000_000;
      return true;
    })
    .filter(o => {
      if (filterIrr === "all") return true;
      const irr = o.irr_pct || 0;
      if (filterIrr === "lt15")  return irr < 15;
      if (filterIrr === "15-25") return irr >= 15 && irr <= 25;
      if (filterIrr === "gt25")  return irr > 25;
      return true;
    }),
  [opps, filterStatus, filterCapex, filterIrr, filterBu, filterSdg]);

  const activeFilterCount = [filterStatus, filterCapex, filterIrr, filterBu, filterSdg]
    .filter(f => f !== "all").length;

  // Dynamic KPI values — reflect filtered set when filters are active
  const viewMetrics = useMemo(() => {
    const src = activeFilterCount > 0 ? filtered : opps;
    const capex    = src.reduce((s,o) => s + (o.capex_usd||0), 0);
    const savings  = src.reduce((s,o) => s + (o.cost_savings_usd_yr||0), 0);
    const circ     = src.reduce((s,o) => s + (o.circularity_value_usd_yr||0), 0);
    const opValue  = savings + circ;
    const carbonBase = src.reduce((s,o) => s + (o.carbon_rev_base_usd_yr||0), 0);
    const redPot   = src.reduce((s,o) => s + (o.reduction_tco2e_yr||0), 0);
    const sdgSet   = new Set(src.flatMap(o => o.sdg_tags||[]));
    const blended  = savings > 0 ? (capex / savings).toFixed(1) + "yr" : "—";
    return { capex, savings, circ, opValue, carbonBase, redPot, sdgSet, blended, count: src.length };
  }, [filtered, opps, activeFilterCount]);

  const clearAllFilters = () => {
    setFilterStatus("all"); setFilterCapex("all");
    setFilterIrr("all"); setFilterBu("all"); setFilterSdg("all");
  };

  if (loading && !vbData) return (
    <div className="p-6 space-y-4">
      {[1,2,3].map(i=><div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse"/>)}
    </div>
  );

  return (
    <div className={`p-6 lg:p-8 space-y-6 ${selected ? "mr-96" : ""}`}>

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Dangote Industries</p>
          <PageHeader title="Carbon Value Bridge" />
          <p className="text-xs text-slate-400 mt-0.5">
            Where is the value? · All data from approved monthly calculation outputs · {period?.label}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={selectedPeriod||""} onChange={e => setSelectedPeriod(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700">
            {periods.map(p=><option key={p.period_id} value={p.period_id}>{p.label}</option>)}
          </select>
          <span className="text-xs font-bold px-2 py-0.5 rounded-full border bg-teal-100 text-teal-700 border-teal-200">
            {byStatus.identified||0} identified · {byStatus.promoted_to_strategy||0} in strategy
          </span>
        </div>
      </div>

      {/* A. Portfolio KPI Strip — dynamic from current view */}
      <div className="space-y-2" data-testid="portfolio-summary">
        {/* Control signal */}
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-semibold text-teal-700 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-500 inline-block" />
            Portfolio validated — consistent with monthly calculation output
          </p>
          {activeFilterCount > 0 && (
            <p className="text-[10px] text-amber-600 font-semibold">
              Current view: {viewMetrics.count} of {opps.length} opportunities
            </p>
          )}
          {activeFilterCount === 0 && (
            <p className="text-[10px] text-slate-400">
              Current view: all {viewMetrics.count} opportunities
            </p>
          )}
        </div>

        {/* KPI cards — 4 per row, elevated */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard
            label="Total Reduction"
            value={`${Number(viewMetrics.redPot/1000).toFixed(0)}K`}
            unit="tCO₂e/yr"
            color="text-emerald-700"
            sub={`Sum of ${viewMetrics.count} items`}
            className="shadow-sm"
          />
          <MetricCard
            label="Operational Value"
            value={`$${Number(viewMetrics.opValue/1e6).toFixed(0)}M`}
            unit="/yr"
            color="text-teal-700"
            sub={`Sum of ${viewMetrics.count} opportunities`}
            className="shadow-sm"
          />
          <MetricCard
            label="Total CAPEX"
            value={`$${Number(viewMetrics.capex/1e6).toFixed(0)}M`}
            color="text-slate-900"
            sub={`Blended payback: ${viewMetrics.blended}`}
            className="shadow-sm"
            testid="total-capex-metric"
          />
          <MetricCard
            label="Indicative Carbon Value"
            value={`$${Number(viewMetrics.carbonBase/1e6).toFixed(0)}M`}
            unit="/yr (conditional)"
            color="text-blue-700"
            sub={`${viewMetrics.sdgSet.size} SDGs: ${[...viewMetrics.sdgSet].sort().join(", ")}`}
            className="shadow-sm"
          />
        </div>
      </div>

      {/* B. A/B Value Band — full-width, portfolio aggregated */}
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <div className="bg-slate-900 px-5 py-2.5 flex items-center justify-between">
          <p className="text-[10px] font-bold text-white uppercase tracking-widest">Portfolio Aggregated Value</p>
          <p className="text-[9px] text-slate-400">
            Aggregated from opportunity-level values — {viewMetrics.count} items
          </p>
        </div>
        <div className="grid grid-cols-2">
          <div className="bg-emerald-50 border-r border-slate-200 p-5">
            <p className="text-[9px] font-bold text-emerald-700 uppercase tracking-wide mb-1">A. Operational Value</p>
            <p className="text-[9px] text-emerald-600 mb-3">High confidence · execution dependent · no validation required</p>
            <p className="text-2xl font-bold text-emerald-800 mb-2">
              ${Number(viewMetrics.opValue/1e6).toFixed(0)}M/yr
            </p>
            {/* Breakdown */}
            <div className="space-y-1 pt-2 border-t border-emerald-200">
              <div className="flex justify-between text-xs">
                <span className="text-emerald-600">Cost savings</span>
                <span className="font-semibold text-emerald-800">${Number(viewMetrics.savings/1e6).toFixed(0)}M/yr</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-emerald-600">Circularity value</span>
                <span className="font-semibold text-emerald-800">${Number(viewMetrics.circ/1e6).toFixed(0)}M/yr</span>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 p-5">
            <p className="text-[9px] font-bold text-blue-700 uppercase tracking-wide mb-1">B. Indicative Carbon Value</p>
            <p className="text-[9px] text-blue-600 mb-3">Conditional · subject to validation + issuance · not in base DSCR</p>
            <p className="text-2xl font-bold text-blue-800 mb-2">
              ${Number(viewMetrics.carbonBase/1e6).toFixed(0)}M/yr
            </p>
            <div className="space-y-1 pt-2 border-t border-blue-200">
              <div className="flex justify-between text-xs">
                <span className="text-blue-600">Low $15/t</span>
                <span className="font-semibold text-blue-700">${Number(viewMetrics.redPot*15/1e6).toFixed(0)}M/yr</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-blue-600">High $40/t</span>
                <span className="font-semibold text-blue-700">${Number(viewMetrics.redPot*40/1e6).toFixed(0)}M/yr</span>
              </div>
            </div>
            <p className="text-[8px] text-red-500 italic mt-2 font-semibold">
              Must NOT be included in base DSCR until VVB validation confirmed
            </p>
          </div>
        </div>
      </div>

      {/* B. Opportunity Table */}
      <SectionCard>
        {/* Table header + status pills */}
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <p className="text-xs text-slate-700 font-bold">
              Opportunity Table
              <span className="font-normal text-slate-400 ml-1">
                — {filtered.length === opps.length
                  ? `all ${opps.length} items`
                  : `${filtered.length} of ${opps.length} items (filtered)`}
              </span>
            </p>
            <p className="text-[9px] text-slate-400 mt-0.5">
              Aggregated from opportunity-level values · each row computed from monthly calculation output
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Status pills */}
            {[
              ["all",                   `All (${opps.length})`],
              ["identified",            `Identified (${byStatus.identified||0})`],
              ["promoted_to_strategy",  `In Strategy (${byStatus.promoted_to_strategy||0})`],
            ].map(([s,l]) => (
              <FilterPill key={s} label={l} active={filterStatus===s} onClick={() => setFilterStatus(s)} />
            ))}
            {/* Advanced filters toggle */}
            <button onClick={() => setShowFilters(v => !v)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${
                showFilters || activeFilterCount > 0
                  ? "bg-slate-700 text-white border-transparent"
                  : "bg-white border-slate-200 text-slate-500 hover:border-slate-400"
              }`}
              data-testid="filters-toggle">
              <Filter size={9}/>
              Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
            </button>
            {activeFilterCount > 0 && (
              <button onClick={clearAllFilters}
                className="text-[10px] text-red-500 hover:text-red-700 font-semibold"
                data-testid="clear-filters">
                Clear all
              </button>
            )}
          </div>
        </div>

        {/* Advanced filter panel */}
        {showFilters && (
          <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2.5" data-testid="filter-panel">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {/* CAPEX range */}
              <div>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">CAPEX Range</p>
                <div className="flex flex-wrap gap-1">
                  {[["all","All"],["lt10","< $10M"],["10-25","$10–25M"],["gt25","$25M+"]].map(([k,l])=>(
                    <FilterPill key={k} label={l} active={filterCapex===k} onClick={() => setFilterCapex(k)} />
                  ))}
                </div>
              </div>
              {/* IRR */}
              <div>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">IRR Tier</p>
                <div className="flex flex-wrap gap-1">
                  {[["all","All"],["lt15","< 15%"],["15-25","15–25%"],["gt25","25%+"]].map(([k,l])=>(
                    <FilterPill key={k} label={l} active={filterIrr===k} onClick={() => setFilterIrr(k)} />
                  ))}
                </div>
              </div>
              {/* BU */}
              <div>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">Business Unit</p>
                <div className="flex flex-wrap gap-1">
                  <FilterPill label="All BUs" active={filterBu==="all"} onClick={() => setFilterBu("all")} />
                  {buOptions.map(bu=>(
                    <FilterPill key={bu.id} label={bu.name?.split(" ").pop()||bu.id}
                      active={filterBu===bu.id} onClick={() => setFilterBu(bu.id)} />
                  ))}
                </div>
              </div>
              {/* SDG */}
              <div>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">SDG</p>
                <div className="flex flex-wrap gap-1">
                  <FilterPill label="All SDGs" active={filterSdg==="all"} onClick={() => setFilterSdg("all")} />
                  {sdgOptions.map(sdg=>(
                    <button key={sdg}
                      onClick={() => setFilterSdg(filterSdg===String(sdg) ? "all" : String(sdg))}
                      className={`text-[10px] font-bold px-2 py-0.5 rounded transition-all border ${
                        filterSdg===String(sdg) ? "text-white border-transparent" : "bg-white border-slate-200 text-slate-600"
                      }`}
                      style={filterSdg===String(sdg) ? {backgroundColor: SDG_COLORS[sdg]||"#666"} : {}}>
                      SDG {sdg}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-100">
                {["#","Opportunity","BU / Asset","Driver","Reduction tCO₂e/yr","Operational $/yr","Carbon $/yr","CAPEX","IRR","Pathway","SDGs","Tag","Status",""].map(h=>(
                  <th key={h} className="text-left text-slate-400 pb-2 pr-3 font-semibold text-[9px] uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(o => (
                <tr key={o.opp_id}
                  data-testid={`opp-row-${o.opp_id}`}
                  className={`border-b border-slate-50 last:border-0 cursor-pointer transition-colors ${
                    selected?.opp_id===o.opp_id ? "bg-teal-50/70" : "hover:bg-slate-50/70"
                  }`}
                  onClick={() => setSelected(o)}>
                  <td className="py-2.5 pr-2 text-center">
                    <span className="text-[10px] font-bold text-slate-500">#{rankMap[o.opp_id]||"—"}</span>
                  </td>
                  <td className="py-2.5 pr-3">
                    <p className="font-semibold text-slate-900 max-w-[180px] truncate">{o.name}</p>
                  </td>
                  <td className="py-2.5 pr-3">
                    <p className="text-slate-600 truncate max-w-[120px]">{o.bu_name?.split(" ").pop()}</p>
                    <p className="text-slate-400 text-[9px]">{o.asset_id}</p>
                  </td>
                  <td className="py-2.5 pr-3 text-slate-500">{o.driver_label}</td>
                  <td className="py-2.5 pr-3 font-bold text-emerald-700 tabular-nums">
                    {Number(o.reduction_tco2e_yr||0).toLocaleString()}
                  </td>
                  <td className="py-2.5 pr-3 font-bold text-teal-700">
                    ${Number((o.guaranteed_value_usd_yr||0)/1e6).toFixed(1)}M
                  </td>
                  <td className="py-2.5 pr-3 text-blue-600">
                    ${Number((o.carbon_rev_base_usd_yr||0)/1e6).toFixed(1)}M
                  </td>
                  <td className="py-2.5 pr-3 text-slate-700 tabular-nums">
                    ${Number((o.capex_usd||0)/1e6).toFixed(0)}M
                  </td>
                  <td className="py-2.5 pr-3 text-emerald-700 font-bold">{o.irr_pct||"—"}%</td>
                  <td className="py-2.5 pr-3">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${PATHWAY_COLORS[o.pathway]||PATHWAY_COLORS.internal}`}>
                      {(o.pathway||"internal").toUpperCase()}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3">
                    <div className="flex gap-0.5">
                      {(o.sdg_tags||[]).slice(0,3).map(sdg => (
                        <span key={sdg} className="text-[9px] font-bold text-white px-1.5 py-0.5 rounded"
                          style={{backgroundColor: SDG_COLORS[sdg]||"#666"}}>
                          {sdg}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-2.5 pr-2">
                    {(() => { const t = getTag(o); return (
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border whitespace-nowrap ${t.cls}`}>{t.label}</span>
                    ); })()}
                  </td>
                  <td className="py-2.5 pr-3">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${STATUS_COLORS[o.status]||STATUS_COLORS.identified}`}>
                      {STATUS_LABELS[o.status]||o.status}
                    </span>
                  </td>
                  <td className="py-2.5 pr-1">
                    {o.status === "identified" ? (
                      <button
                        onClick={e => { e.stopPropagation(); handleTransition(o.opp_id,"promoted_to_strategy"); }}
                        className="px-2 py-1 bg-[#0F172A] text-white text-[9px] font-bold rounded-lg hover:bg-slate-700 whitespace-nowrap"
                        data-testid={`promote-btn-${o.opp_id}`}>
                        → Strategy
                      </button>
                    ) : (
                      <ChevronRight size={11} className="text-slate-300" />
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={12} className="py-8 text-center text-slate-400 text-xs">
                    No opportunities match the selected filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* C. Scenario View */}
      {opps.length > 0 && <ScenarioView opps={opps} />}

      {/* Pipeline navigation */}
      <SectionCard>
        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Pipeline Navigation</p>
        <div className="flex items-center gap-2 flex-wrap text-xs">
          {[
            ["← Climate & Circularity", "/corporate/climate"],
            ["← SDG Contribution",      "/corporate/sdg"],
            ["Carbon Strategy →",        "/corporate/carbon-strategy"],
            ["Carbon Structuring →",     "/corporate/carbon-structuring"],
            ["Financing →",              "/corporate/financing"],
          ].map(([l,p]) => (
            <button key={p} onClick={() => navigate(p)}
              className="px-3 py-1.5 border border-slate-200 text-slate-600 rounded-xl hover:border-teal-400 hover:text-teal-700 transition-colors text-xs font-semibold">
              {l}
            </button>
          ))}
        </div>
      </SectionCard>

      {/* Detail panel overlay */}
      {selected && (
        <>
          <div className="fixed inset-0 bg-black/10 z-40" onClick={() => setSelected(null)} />
          <DetailPanel
            opp={selected}
            onClose={() => setSelected(null)}
            onTransition={handleTransition}
            transitioning={transitioning}
          />
        </>
      )}
    </div>
  );
}
